import { useEffect } from 'react';
import {
  ActionIcon,
  Badge,
  Container,
  Group,
  Skeleton,
  Tabs,
  Text,
  Title,
} from '@buildex/ui';
import { useUnit } from 'effector-react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { $currentTenant, $tenantsLoading, tenantOpened } from '@/entities/tenant/model';
import { ModuleToggles } from '@/widgets/module-toggles/ModuleToggles';
import { BrandingEditor } from '@/widgets/branding-editor/BrandingEditor';
import { TenantUsers } from '@/widgets/tenant-users/TenantUsers';
import { RoleBuilder } from '@/widgets/role-builder/RoleBuilder';

export function TenantDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { tenant, loading, open } = useUnit({
    tenant: $currentTenant,
    loading: $tenantsLoading,
    open: tenantOpened,
  });

  useEffect(() => {
    if (tenantId !== undefined) open(tenantId);
  }, [tenantId, open]);

  if (loading || (tenant === null && tenantId !== undefined)) {
    return (
      <Container fluid>
        <Skeleton height={40} w={320} mb="md" />
        <Skeleton height={400} radius="lg" />
      </Container>
    );
  }

  if (tenant === null) return null;

  return (
    <Container fluid>
      <Group gap="sm" mb="md">
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/tenants')} aria-label={t('common.back')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M19 12H5m0 0 6-6m-6 6 6 6" />
          </svg>
        </ActionIcon>
        <div>
          <Group gap="xs">
            <Title order={3}>{tenant.name}</Title>
            <Badge color={tenant.active ? 'teal' : 'red'}>
              {tenant.active ? t('tenants.active') : t('tenants.blocked')}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">
            {tenant.slug}.buildex.com
          </Text>
        </div>
      </Group>

      <Tabs defaultValue="modules" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="modules">{t('menu.modules')}</Tabs.Tab>
          <Tabs.Tab value="branding">{t('menu.branding')}</Tabs.Tab>
          <Tabs.Tab value="users">{t('menu.users')}</Tabs.Tab>
          <Tabs.Tab value="roles">{t('menu.roles')}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="modules">
          <ModuleToggles tenant={tenant} />
        </Tabs.Panel>
        <Tabs.Panel value="branding">
          <BrandingEditor tenant={tenant} />
        </Tabs.Panel>
        <Tabs.Panel value="users">
          <TenantUsers tenant={tenant} />
        </Tabs.Panel>
        <Tabs.Panel value="roles">
          <RoleBuilder tenant={tenant} />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
}
